import React, { useEffect, useRef, useState } from "react";
import Script from "next/script";
import { useIntersectionObserver } from "usehooks-ts";

import { buildAdTag } from "@/utils/googleAds/buildAdTag";
import { getMergedTargeting } from "@/utils/googleAds/getMergedTargeting";
import { kvsToCustParams } from "@/utils/googleAds/kvsToCustParams";

type Props = {
  playerId: string;
  playerScript: string;
  playlist?: string;
  file?: string;
  enableAds?: boolean;
  preferSlotId?: string;
  adUnitPath?: string;
};

const Video = ({
  playerId,
  playerScript,
  playlist,
  file,
  enableAds = true,
  preferSlotId,
  adUnitPath = "/13212770/cms/news_content",
}: Props) => {
  if (!playlist && !file) {
    throw new Error("Video component must have playlist or file defined");
  }
  // We need to ensure the script is loaded, if the video is in view
  // there can be a race condition where the script is not loaded yet
  // and the player is initialized before the script is loaded.
  const [isPlayerScriptLoaded, setIsPlayerScriptLoaded] = useState(false);

  // to ensure we only load the player once when in view
  const { ref, isIntersecting } = useIntersectionObserver({
    freezeOnceVisible: true,
  });

  // using a ref instead of the id to initialize the player
  // to avoid race conditions when trying to setup the player.
  const playerRef = useRef<HTMLDivElement>(null);

  // Store built ad tag (if ads enabled)
  const [adTagUrl, setAdTagUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!enableAds) {
      return;
    }

    const pageUrl =
      typeof window !== "undefined"
        ? window.location.href
        : "https://www.leafly.com";

    const googletag = window.googletag;

    const buildAndSetAdTag = () => {
      const kvs = getMergedTargeting({ preferSlotId });
      const custParams = kvsToCustParams(kvs);

      const tag = buildAdTag({
        adUnitPath,
        custParams: custParams || undefined,
        pageUrl,
      });
      setAdTagUrl(tag);
    };

    // If GPT isn't ready yet, queue it; otherwise run immediately.
    if (googletag?.cmd?.push) {
      googletag.cmd.push(buildAndSetAdTag);
    } else {
      buildAndSetAdTag();
    }
  }, [enableAds, preferSlotId, adUnitPath]);

  useEffect(() => {
    if (
      isIntersecting &&
      isPlayerScriptLoaded &&
      playerRef.current &&
      !!window.jwplayer
    ) {
      // For more information about the JWPlayer docs:
      // https://docs.jwplayer.com/players/reference/configuration-options-getting-started
      window.jwplayer(playerRef.current).setup({
        file,
        playlist,

        // Inject IMA advertising only if ads enabled
        ...(enableAds && adTagUrl
          ? {
              advertising: {
                client: "googima",
                schedule: [{ offset: "pre", tag: adTagUrl }],
              },
            }
          : {}),
      });
    }
  }, [
    isPlayerScriptLoaded,
    isIntersecting,
    file,
    playlist,
    enableAds,
    adTagUrl,
  ]);

  return (
    <>
      {/* For more information about the cloud-hosted player:
        https://docs.jwplayer.com/players/docs/add-a-cloud-hosted-player */}
      <Script
        src={playerScript}
        id={`jwplayer-script-${playerId}`}
        onLoad={() => setIsPlayerScriptLoaded(true)}
      />
      <div ref={ref} className="bg-leafly-white aspect-video relative">
        <div className="absolute w-full h-full">
          <div id={playerId} ref={playerRef} />
        </div>
      </div>
    </>
  );
};

export default Video;
